import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseService } from 'app/@core/services/base.service';
import { StorageService } from 'app/@core/services/storage.service';
import { IUpdatedPreferenceJson, IPreferenceJson } from 'app/shared/interfaces/companyData.interface';
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
@Component({
  selector: 'customr-login',
  templateUrl: './customr-login.component.html',
  styleUrls: ['./customr-login.component.scss']
})
export class CustomrLoginComponent implements OnInit {
  isPasswordVisible: boolean = false;
  company: string;
  companyData: IUpdatedPreferenceJson;
  loginForm: FormGroup;
  shipmentRefrenceNumber: number;
  shipmentSecondaryTypes: any;
  activeSection: 'track' | 'login' = 'track';
  isDesktop = false;
  showTooltip = false;
  isMobile = false;
  fallbackLogo = 'assets/img/logo-visibility.png'
  fallbackCompanyBg = 'assets/pin-icons/ezcheck.jpg' 
  constructor(private baseService: BaseService, private activatedRoute: ActivatedRoute, private toastr: ToastrService,
    private fb: FormBuilder, private router: Router, private storageService: StorageService, private spinner: NgxSpinnerService, private breakpoint: BreakpointObserver,) {
    // Remove isLogout or resetToDefault Param becuase we have already cleared local Storage in resolver. Not removing this will make the authGuard clear the local Storage when we successfully log in and route to modules page which will in turn route us back to default login page.
    this.router.navigate(
      [window.location.pathname],
      {
        replaceUrl: true,
        queryParams: {
          'isLogout': null,
          'resetToDefault': null
        },
        queryParamsHandling: 'merge'
      }
    );
  }

  // -------------------------------- Hooks ------------------------------------------------
  ngOnInit(): void {
    this.isMobile = window.innerWidth < 1024;
    this.activatedRoute.data.subscribe(data => {
      const companyData = data['companyData'];
      if (companyData) {
        this.companyData = companyData
        this.setActiveSection()
        localStorage.setItem('companyPrefrences', JSON.stringify(this.companyData));
        this.getSecondaryTypeNames(this.companyData.companyId);
      }
    });
    this.initLoginForm();
    this.checkViewport();

    window.addEventListener('resize', () => { this.checkViewport(), this.isMobile = window.innerWidth < 768; });
  }


  onHover(state: boolean): void {
    if (!this.isMobile) {
      this.showTooltip = state;
    }
  }

  onClick(): void {
    if (this.isMobile) {
      this.showTooltip = !this.showTooltip;
    }
  }
  // ---------------------------------------- Private Methods ------------------------------
  /**
   * Initilizing Login Form 
  */
  private initLoginForm(): void {
    this.loginForm = this.fb.group({
      loginEmail: ['', [
        Validators.required,
        Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'),
      ]],
      loginPassword: ['', Validators.required],
    });
  }
  // -------------------------------- Public Methods --------------------------------------

  /**
   * Get Form Control 
  */
  getControl(control: string): FormControl {
    return this.loginForm.get(control) as FormControl;
  }

  /**
   * Toggle Password Visibility
  */
  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  /**
   * Submit Login Form
  */
  submitLoginForm() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    // match mobile widths under 768px
    const isMobile = this.breakpoint.isMatched('(max-width: 1028px)');
    if (isMobile) {
      this.toastr.warning(
        'Login is only available on laptops and desktops. Kindly switch to a larger screen device.',
        'Login Unavailable'
      );
      return;
    }
    this.spinner.show(undefined,
      {
        type: 'ball-scale-multiple',
        size: 'medium',
        bdColor: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        fullScreen: true
      });
    this.baseService.postRequest('user/login', this.loginForm.value).pipe(
      finalize(() => this.spinner.hide())
    ).subscribe({
      next: (res: any) => {
        if (res.statusCode === 200) {
          const company = res.data?.company;
          const token = res.data?.token;
          const companyPrefrences = localStorage.getItem('companyPrefrences');
          const parsedCompanyPrefrences = companyPrefrences ? JSON.parse(companyPrefrences) : null;
          let origin = encodeURIComponent(window.location.origin);
          if (company?.isMultipleSubscription) {
            localStorage.setItem('origin', origin);
            localStorage.setItem("userType", res.data.company.userType)
            localStorage.setItem("userId", company.companyId);
            localStorage.setItem("isMultipleSubscription", company.isMultipleSubscription?.toString());
            localStorage.setItem("companyID", company.companyId.toString());
            localStorage.setItem("authToken", token);
            this.storageService.setCompanyName(company.companyName);
            this.storageService.setUserEmail(company.email);
            localStorage.setItem("subscription", JSON.stringify(company.subscription));
            localStorage.setItem('isCustomer', JSON.stringify(true))
            if (res.data.company.userType === "Company User") {
              localStorage.setItem('com_Employee_Id', res.data.company.com_Employee_Id);
              localStorage.setItem('companyUserId', res.data.company.companyUserId||'');

            }
            this.router.navigate(['/modules']);
          } else {
            const module = company.subscription[0];
            const queryParams = {
              companyId: company.companyId,
              companyUserId: company.companyUserId||'',
              moduleId: module.moduleId,
              userEmail: company.email,
              companyName: company.companyName,
              userName: company.companyName,
              authToken: token,
              subscriptionId: module.subscriptionId,
              isMultipleSubscription: company.isMultipleSubscription,
              userType: company.userType,
              companyNickName: parsedCompanyPrefrences?.nickName || '',
              isCustomer: true,
              origin:origin
            }
            if (res.data.company.userType === "Company User") {
              queryParams['com_Employee_Id'] = res.data.company.com_Employee_Id;
            }
            const route = module?.url;
            const urlWithParams = this.constructUrlWithParams(route, queryParams);
            window.location.href = urlWithParams;
          }
        } else {
          this.toastr.info(res.message);
        }
      }
    });
  }

  /**
   * Construct Url For Navigation To module
  */
  private constructUrlWithParams(route: string, params: any): string {
    const queryString = Object.keys(params).map(key => key + '=' + encodeURIComponent(params[key])).join('&');
    return route + '?' + queryString;
  }

  /**
   * Track Shipment By Refrence Number
  */
  trackShipment() {
    this.spinner.show(undefined,
      {
        type: 'ball-scale-multiple',
        size: 'medium',
        bdColor: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        fullScreen: true
      }
    );
    const queryParams = new HttpParams()
      .set('companyId', this.companyData.companyId)
      .set('srnInput', this.shipmentRefrenceNumber || '');
    this.baseService.getRefNumbers('Shipment/get-shipment-reference-numbers', queryParams).subscribe(
      {
        next: (response) => {
          // Check if **all** responses end with "Not Found"
          // const allNotFound = response.every(item => item.includes('Not Found'));
          // if (allNotFound) {
          //   this.toastr.error('Shipment not found');
          // } else {
          //   // ✅ Navigate with ref numbers in route
          //   const refNumbers = response.join(',');
          //   this.router.navigate(['/track', refNumbers]);
          // }
          localStorage.setItem('userEnteredSecondaryRefNums', JSON.stringify(this.shipmentRefrenceNumber));
          const allNotFound = response.every(shipment =>
            shipment.srNs.every(sr => sr.value === 'Not Found')
          );

          if (allNotFound) {
            this.spinner.hide();
            this.toastr.error('Shipment not found');
            return
          }
          // const refNumbers = response.map((item: any) => item.primaryReferenceNumber);
          const refNumbers = response.map((item: any) => {
            const isNotFound = item.srNs.every((sr: any) => sr.value === 'Not Found');
            if (isNotFound) {
              item.primaryReferenceNumber = item.primaryReferenceNumber + 'notfound';
            }
            return item.primaryReferenceNumber
          });

          const convertToString = refNumbers.join(',');
          localStorage.setItem('searchedShipments', JSON.stringify(response));
          this.router.navigate(['/track', convertToString]);

          this.spinner.hide();
        },
        error: () => {
          this.toastr.error('This shipment is not available.');
          this.spinner.hide();
        }
      }
    )
  }


  // Fetch Secondary Reference Types 
  getSecondaryTypeNames(companyID: number) {
    if (!companyID) {
      return;
    }
    const payload = {
      pageNumber: 1,
      pageSize: 20,
      companyId: companyID,
      filterJson: "",
      filters: [],
      userId: '',
      sort: {}
    }
    this.baseService.postRequest('get-reference-type', payload).subscribe({
      next: (response) => response ? this.shipmentSecondaryTypes = response.data : [],
      error: () => { }
    })
  }


  // Filter and Format Secondary Reference Types
  formatSecondaryTypeNames() {
    return this.shipmentSecondaryTypes?.filter((type: any) => type.isActive).map(t => t.name).join(', ') ?? '';
  }

  // Check Viewport 
  checkViewport() {
    this.isDesktop = window.innerWidth >= 1028; // md breakpoint
  }

  /**
   * Forgot Password
   */
  forgotPassword() {
    localStorage.setItem('isCustomer', JSON.stringify(true))
    this.router.navigateByUrl('/forgot-password');
  }

  // Checking if the company has public shipment tracking enabled.
  checkIfTrackingEnabled() {
    return this.companyData?.isShipmentTracking === 1;
  }

  // Checking if the company has login access enabled.
  checkIfLoginAccessEnabled() {
    return this.companyData?.isLoginAccess === 1 || this.companyData?.isEztrackLoginAccess === 1;
  }

  isVantixCompany(): boolean {
    return this.activatedRoute.snapshot.paramMap.get('company')?.toLowerCase() === 'vantix';
  }

  // In the event that the company does not have access to shipment tracking, change the active section to login to render it by default on smaller screens.   
  setActiveSection() {
    if (!this.checkIfTrackingEnabled())
      this.activeSection = 'login'
  }

  // Check if single section is rendering 
  isSingleSection(): boolean {
    if (!this.companyData) {
      return false;
    }
    const { isLoginAccess, isEztrackLoginAccess, isShipmentTracking } = this.companyData;
    const loginAccess = isLoginAccess === 1;
    const eztrackAccess = isEztrackLoginAccess === 1;
    const shipment = isShipmentTracking === 1;

    const isSingle =
      (shipment && (!loginAccess && !eztrackAccess)) ||
      (!shipment && (loginAccess || eztrackAccess));

    return isSingle;
  }

}
